/**
 * The anonymous, stable per-browser correlation id (logging-strategy
 * POLICY.md §3's browser-correlation half; authenticated call sites add
 * `userId` separately).
 *
 * Built on this app's own ../storage.ts (`resolveLocalStorage`/`storageKey`),
 * the same conventions src/install/state.ts already uses: the storage handle
 * is an injected parameter defaulting to `resolveLocalStorage()`, so this
 * module imports cleanly under the prerender and under vitest, neither of
 * which has a `localStorage` to reach for.
 *
 * Minted once with @bindery/logging's `randomUUID` and persisted; every
 * later call reads the same value back. Despite the name, this does NOT
 * rotate per browser session — it is one id per browser, forever, until
 * storage is cleared. True per-visit session correlation is a future
 * enhancement; until then, treat this as a stable anonymous browser id
 * rather than a session boundary.
 *
 * The mint goes through that helper rather than `crypto.randomUUID` directly
 * because this module is called from a `useState` initializer in
 * LoggerProvider.tsx, where a throw takes the React tree with it — and
 * because a function whose no-storage branch is written never to throw has
 * no business throwing on the very line that branch returns.
 */
import { randomUUID } from "@bindery/logging";
import { resolveLocalStorage, type StorageLike, storageKey } from "../storage";

export const SESSION_ID_KEY = storageKey("logging.sessionId.v1");

/**
 * The one id this page load mints, shared by every call that has to mint.
 *
 * Deliberate module state, and the only state in this module. Without it,
 * `getSessionId()` mints a fresh id on every call whenever storage is
 * unavailable — Chromium set to block all cookies makes touching
 * `localStorage` throw, so `resolveLocalStorage()` returns null (see
 * ../storage.ts) and the branch below has nothing to read a previous id back
 * from. Each caller would then report a different `sessionId` for one visit;
 * createClientLogger.ts in particular calls `getSessionId()` once per
 * factory call and freezes the result onto every event that logger emits, so
 * "an id per call" is really "an id per logger". A query grouping by
 * `sessionId` could no longer reconstruct the visit — for exactly the users
 * whose browsers are hardest to debug otherwise.
 *
 * The persisted path is unaffected: a mint that reaches storage is read back
 * by every later call the way it always was, and this memo only ever agrees
 * with it.
 */
let mintedForThisLoad: string | null = null;

function mintSessionId(): string {
  mintedForThisLoad ??= randomUUID();
  return mintedForThisLoad;
}

/**
 * Forget the id minted for this page load, so the next mint is a fresh one.
 *
 * Test-only seam. A page load has exactly one such id and no app code has a
 * reason to ask for a second; tests need it because vitest shares one module
 * instance across every case in a file, which would otherwise make each
 * case's id depend on which cases ran before it.
 */
export function resetMintedSessionId(): void {
  mintedForThisLoad = null;
}

/**
 * Read the persisted session id, minting and persisting a new one on first
 * call. A storage read/write failure (quota, private mode) degrades to
 * minting an unpersisted id for this page load only — the direction every
 * read in this module errs: never throw, never block logging. That degraded
 * id is the same one on every call, per `mintedForThisLoad` above.
 */
export function getSessionId(
  storage: StorageLike | null = resolveLocalStorage(),
): string {
  if (storage === null) return mintSessionId();

  let existing: string | null;
  try {
    existing = storage.getItem(SESSION_ID_KEY);
  } catch {
    existing = null;
  }
  if (existing) return existing;

  const minted = mintSessionId();
  try {
    storage.setItem(SESSION_ID_KEY, minted);
  } catch {
    // Quota, or a private-mode write failure. The id still works for this
    // page load; it just won't survive a reload.
  }
  return minted;
}
